/* eslint-disable react/prop-types */
import { useContext, useEffect, useState } from "react";
import { Text } from "flicket-ui";
import { useRouter } from "next/router";
import { Layout, Seo, Status } from "~components";
import {
  Header,
  NonSeated as NonSeatedComponent,
} from "~components/reservation";
import { BaseContext } from "~context";
import { useNonSeatedReservation } from "~hooks";
import { getEvent } from "~graphql/fetchers";
import { formatDate, getError, getUrl, showToast } from "~lib";

import useSWR from "swr";
import { AccessModal } from "~components/releases";
import { TicketDetailsModal } from "~features/seated-reservation/components/common/modal.TicketDetails";
import { Seated as SeatedReservation } from "~features/seated-reservation/reservation.Seated";
import * as actions from "~features/seated-reservation/store/actions/index";
import { QueryParams } from "~features/seated-reservation/types";
import {
  EventAccessStatus,
  EventKind,
  EventQuery,
  ReleaseStatusValue,
  VenueSeating,
} from "~graphql/sdk";
import omit from "lodash/omit";
import HeaderAlert from "~components/common/common.HeaderAlert";
import { useCampaignReportingOpen } from "~hooks/useBroadastCampaignReporting";
import { useReferralCode } from "~hooks/useReferralCode";
import {
  googleAnalyticsReleaseAtom,
  useGoogleAnalyticsPageView,
} from "~hooks/useGoogleAnalytics";
import { GetServerSideProps, NextPage } from "next";
import {
  CompetitionSeoProps,
  competitionSeo,
  asOpenGraphImage,
} from "~features/competitions/competitionSeo";
import { store } from "~lib/store";
import { useAtom } from "jotai";
import { useEventAccessControl } from "~hooks/useEventAccessControl";
import { useFacebookPixelScripts } from "~features/analytics/useThirdpartyScripts";
import { ReleaseType } from "../../../graphql/typed-document-nodes";

type Query = {
  eventId?: string;
  release?: string;
  slug?: string;
  ownedTickets?: string;
  session?: string;
  posId?: string;
  customer?: string;
  zone?: string;
};

const NoAccessAlert = () => {
  return (
    <Text
      py="1/2"
      px={2}
      bg="S300"
      color="white"
      textAlign="center"
      fontWeight="extraBold"
      w="100%"
      zIndex={1000}
    >
      Note: This release is not accessible to the public
    </Text>
  );
};

export const getServerSideProps: GetServerSideProps<CompetitionSeoProps> = async (
  context
) => {
  return await competitionSeo(
    store.organization.id,
    context.params?.eventId as string
  );
};

const NonSeatedReservation = ({
  type,
  typeId,
  isDigital,
  isAdmin,
}: {
  type: "event" | "membership";
  typeId: string;
  isDigital: boolean;
  isAdmin: boolean;
}) => {
  const router = useRouter();
  const query = router.query as Query;

  const {
    event,
    handleGAOrderCreation,
    selectableZones,
    release,
  } = useNonSeatedReservation({
    type,
    id: typeId,
    releaseId: query.release,
    slug: query.slug,
  });

  const showAllZones = !query?.zone;

  return (
    <NonSeatedComponent
      isAdmin={isAdmin}
      isDigital={isDigital}
      zones={selectableZones.filter((item) => {
        if (showAllZones) return item;

        const name = toStandardString(item.name);

        if (Array.isArray(query.zone)) {
          return query.zone.map(toStandardString).includes(name);
        }

        return toStandardString(query.zone) === name;
      })}
      addons={release?.releaseEventAddons.map(({ eventAddon }) => eventAddon)}
      eventOrMembership={event}
      createOrder={handleGAOrderCreation}
      releaseNotes={release?.releaseNotes}
      allowPublicComp={release?.allowPublicComp}
      isResaleRelease={release?.isResaleRelease}
      releaseType={release?.type}
      requiresAuth={release?.requiresAuth}
    />
  );
};

const Reservation = ({ event }: { event: EventQuery["event"] }) => {
  const router = useRouter();
  const query = router.query as Query;
  const { organization } = useContext(BaseContext);

  const [hasAccess, setHasAccess] = useState<boolean | undefined>(undefined);
  const {
    error,
    release,
    isAdmin,
    isLoading,
    accessType,
  } = useEventAccessControl();
  // Store referralCode & version in sessionStorage.
  useReferralCode();

  const [, setRelease] = useAtom(googleAnalyticsReleaseAtom);

  useEffect(() => {
    if (release) {
      //  @ts-expect-error atom type issue while using strict:false
      setRelease(release);
    }
  }, [release]);

  const [triggerCampaignReporting, setTriggerCampaignReporting] = useState(
    false
  );

  useCampaignReportingOpen(triggerCampaignReporting);

  useEffect(() => {
    void router.prefetch("/checkout/[orderId]");
    void router.prefetch("/events/[eventId]");
  }, []);

  useEffect(() => {
    if (router.query?.session !== QueryParams.SessionContinue) {
      actions.resetState();
    }
  }, []);

  /**
   * This effect handles all of the redirects back to the info page, making sure
   * the the query params stay the same.
   */
  useEffect(() => {
    if (isLoading) {
      return;
    }
    let redirect = false;

    // Take all params except the `eventId` because this is part of the url
    let queryParams: Query = omit(query, "eventId");

    if (release?.status.value === ReleaseStatusValue.ReleaseHasEndedDueToDate) {
      queryParams = omit(queryParams, "slug");
    }

    const canViewInactiveRelease: boolean = isAdmin || !!query.posId;

    const isAccessDenied =
      getError(error, "graphQL") === EventAccessStatus.Denied ||
      (accessType !== EventAccessStatus.Allowed &&
        accessType !== EventAccessStatus.AllowedAdminPreview);

    const hasEventEnded =
      getError(error, "graphQL") === EventAccessStatus.EventEnded;

    if (error && !isAccessDenied && !hasEventEnded) {
      if (query.posId) {
        showToast(getError(error, "graphQL"), "error");
        return window.location.reload();
      }
      showToast(getError(error, "graphQL"), "error");
      redirect = true;
    } else if (
      isAccessDenied ||
      !release ||
      (release?.isActive === false && canViewInactiveRelease === false)
    ) {
      redirect = true;
      showToast(release?.status.message as string, "error");
    }

    if (redirect) {
      void router.push({ pathname: `/events/${event.id}`, query: queryParams });
    } else {
      setTriggerCampaignReporting(true);
    }
  }, [event?.id, release, error, isAdmin, isLoading]);

  useEffect(() => {
    if (release?.requiresAuth !== undefined) {
      setHasAccess(!release.requiresAuth);
    }
  }, [release?.requiresAuth]);

  return (
    <>
      <Header
        title={event.title}
        startDate={event.startDate}
        endDate={event.endDate}
        location={
          event.venue?.name &&
          `${event.venue?.name}, ${event.venue?.address?.city}`
        }
        goBack={() => {
          if (query.posId) {
            window.location.href = `${getUrl(organization.slug)}/pos/${
              query.posId
            }`;
          } else {
            void router.push("/");
          }
        }}
        accessibility={event.accessibility}
        supportInfo={organization?.supportInfo}
        image={event.logo}
        description={event.description}
        coordinates={`${event.venue?.location?.lat},${event.venue?.location?.lng}`}
      />
      <Layout layoutType="sub">
        {isAdmin && release?.isActive === false && <NoAccessAlert />}
        {isAdmin && (
          <HeaderAlert
            message={
              accessType === EventAccessStatus.AllowedAdminPreview
                ? "Event preview"
                : release?.type === ReleaseType.Presale
                ? "Presale preview"
                : "Back office order"
            }
            fixed={event.venue?.seating !== VenueSeating.Seated}
          />
        )}
        <Status error={error} loading={isLoading}>
          {!isLoading && (isAdmin || (release?.isActive && hasAccess)) && (
            <>
              {event.venue?.seating !== VenueSeating.Seated ? (
                <NonSeatedReservation
                  isAdmin={isAdmin}
                  type="event"
                  typeId={query.eventId}
                  isDigital={event.eventKind === EventKind.Digital}
                />
              ) : (
                <SeatedReservation
                  type="event"
                  typeId={query.eventId}
                  release={release}
                  defaultShowOptions={
                    !query.ownedTickets && !query.session && hasAccess
                  }
                />
              )}
              <TicketDetailsModal />
            </>
          )}
          {/* {release?.requiresAuth && !isAdmin && ( */}
          <AccessModal
            hasAccess={hasAccess}
            setHasAccess={setHasAccess}
            release={release}
          />
          {/* )} */}
        </Status>
      </Layout>
    </>
  );
};

const ReservationPage: NextPage<CompetitionSeoProps> = ({
  activeCompetition: { data: activeCompetition },
  eventImageUrls: { data: eventImageUrls },
  eventTitle: { data: eventTitle },
}) => {
  const router = useRouter();
  const { organization } = useContext(BaseContext);
  const { eventId } = router.query as Query;
  useGoogleAnalyticsPageView(eventId);

  const { error, data: event } = useSWR(
    ["event", organization.id, eventId],
    getEvent
  );

  useFacebookPixelScripts(event?.pixelId);

  const isLoading = !event && !error;

  const title = activeCompetition
    ? activeCompetition.title
    : "Get your tickets now!";

  return (
    <>
      <Seo
        title="Tickets"
        description="Tickets"
        options={{
          openGraph: {
            title,
            description: eventTitle,
            images: eventImageUrls?.map((url) =>
              asOpenGraphImage(url, eventTitle)
            ),
          },
        }}
      />
      <Status error={error} loading={isLoading || !event?.venue?.seating}>
        <Reservation event={event} />
      </Status>
    </>
  );
};

export default ReservationPage;

function toStandardString(str: string) {
  return str.trim().toLowerCase().replace(/\s/g, "");
}
